import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Initial state
const initialState = {
    carriersList: [],
    carriersLoading: false,
    carriersError: null,
    selectedCarrier: null,
    selectedCarrierLoading: false,
    selectedCarrierError: null,
    createCarrierLoading: false,
    createCarrierError: null,
    updateCarrierLoading: false,
    updateCarrierError: null,
    deleteCarrierLoading: false,
    deleteCarrierError: null
};

// Async actions
export const getCarriersList = createAsyncThunk(
    "carriers/getCarriersList",
    async ({ accountId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/carriers/carriersList`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    accountId
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero dei corrieri");
        }
    }
);

export const getCarrierDetail = createAsyncThunk(
    "carriers/getCarrierDetail",
    async ({ carrierId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/carriers/`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    id: carrierId
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero dei dettagli del corriere");
        }
    }
);

export const createCarrier = createAsyncThunk(
    "carriers/createCarrier",
    async ({ carrier, token }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL || ''}/carriers/`, {
                carrier
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nella creazione del corriere");
        }
    }
);

export const updateCarrier = createAsyncThunk(
    "carriers/updateCarrier",
    async ({ carrier, token }, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${process.env.REACT_APP_API_BASE_URL || ''}/carriers/`, {
                carrier
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nell'aggiornamento del corriere");
        }
    }
);

export const deleteCarrier = createAsyncThunk(
    "carriers/deleteCarrier",
    async ({ carrierId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL || ''}/carriers/`, {
                headers: {
                    "auth-token": token
                },
                data: {
                    id: carrierId
                }
            });
            return carrierId;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nell'eliminazione del corriere");
        }
    }
);

// Slice
const carriersSlice = createSlice({
    name: "carriers",
    initialState,
    reducers: {
        resetSelectedCarrier: (state) => {
            state.selectedCarrier = null;
            state.selectedCarrierError = null;
        },
        resetCreateStatus: (state) => {
            state.createCarrierLoading = false;
            state.createCarrierError = null;
        },
        resetUpdateStatus: (state) => {
            state.updateCarrierLoading = false;
            state.updateCarrierError = null;
        },
        resetDeleteStatus: (state) => {
            state.deleteCarrierLoading = false;
            state.deleteCarrierError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Gestione getCarriersList
            .addCase(getCarriersList.pending, (state) => {
                state.carriersLoading = true;
                state.carriersError = null;
            })
            .addCase(getCarriersList.fulfilled, (state, action) => {
                state.carriersLoading = false;
                state.carriersList = action.payload;
            })
            .addCase(getCarriersList.rejected, (state, action) => {
                state.carriersLoading = false;
                state.carriersError = action.payload;
            })
            
            // Gestione getCarrierDetail
            .addCase(getCarrierDetail.pending, (state) => {
                state.selectedCarrierLoading = true;
                state.selectedCarrierError = null;
            })
            .addCase(getCarrierDetail.fulfilled, (state, action) => {
                state.selectedCarrierLoading = false;
                state.selectedCarrier = action.payload;
            })
            .addCase(getCarrierDetail.rejected, (state, action) => {
                state.selectedCarrierLoading = false;
                state.selectedCarrierError = action.payload;
            })
            
            // Gestione createCarrier
            .addCase(createCarrier.pending, (state) => {
                state.createCarrierLoading = true;
                state.createCarrierError = null;
            })
            .addCase(createCarrier.fulfilled, (state, action) => {
                state.createCarrierLoading = false;
                if (state.carriersList) {
                    state.carriersList.push(action.payload);
                }
            })
            .addCase(createCarrier.rejected, (state, action) => {
                state.createCarrierLoading = false;
                state.createCarrierError = action.payload;
            })
            
            // Gestione updateCarrier
            .addCase(updateCarrier.pending, (state) => {
                state.updateCarrierLoading = true;
                state.updateCarrierError = null;
            })
            .addCase(updateCarrier.fulfilled, (state, action) => {
                state.updateCarrierLoading = false;
                const updatedIndex = state.carriersList.findIndex(c => c._id === action.payload._id);
                if (updatedIndex !== -1) {
                    state.carriersList[updatedIndex] = action.payload;
                }
                state.selectedCarrier = action.payload;
            })
            .addCase(updateCarrier.rejected, (state, action) => {
                state.updateCarrierLoading = false;
                state.updateCarrierError = action.payload;
            })
            
            // Gestione deleteCarrier
            .addCase(deleteCarrier.pending, (state) => {
                state.deleteCarrierLoading = true;
                state.deleteCarrierError = null;
            })
            .addCase(deleteCarrier.fulfilled, (state, action) => {
                state.deleteCarrierLoading = false;
                state.carriersList = state.carriersList.filter(c => c._id !== action.payload);
            })
            .addCase(deleteCarrier.rejected, (state, action) => {
                state.deleteCarrierLoading = false;
                state.deleteCarrierError = action.payload;
            });
    }
});

// Export actions
export const { 
    resetSelectedCarrier, 
    resetCreateStatus, 
    resetUpdateStatus, 
    resetDeleteStatus 
} = carriersSlice.actions;

// Export selectors
export const selectCarriersList = (state) => state.carriers.carriersList;
export const selectCarriersLoading = (state) => state.carriers.carriersLoading;
export const selectCarriersError = (state) => state.carriers.carriersError;
export const selectSelectedCarrier = (state) => state.carriers.selectedCarrier;
export const selectSelectedCarrierLoading = (state) => state.carriers.selectedCarrierLoading;
export const selectSelectedCarrierError = (state) => state.carriers.selectedCarrierError;
export const selectCreateCarrierLoading = (state) => state.carriers.createCarrierLoading;
export const selectCreateCarrierError = (state) => state.carriers.createCarrierError;
export const selectUpdateCarrierLoading = (state) => state.carriers.updateCarrierLoading;
export const selectUpdateCarrierError = (state) => state.carriers.updateCarrierError;
export const selectDeleteCarrierLoading = (state) => state.carriers.deleteCarrierLoading;
export const selectDeleteCarrierError = (state) => state.carriers.deleteCarrierError;

// Export reducer
export default carriersSlice.reducer;
